/**
 * @typedef {import('../../helpers/analysisHelpers/detectSuspectSubmissions').SuspectSubmission} SuspectSubmission
 */

/**
 * Eén invulling met haar antwoorden naast elkaar, zodat de lezer de
 * tegenstrijdigheid ziet in plaats van hem te moeten geloven.
 *
 * @param {object} props
 * @param {SuspectSubmission} props.item - De gesignaleerde invulling.
 * @param {string} [props.extra] - Regel met het waarom (gat, gemiddelde).
 */
const SuspectRow = ({ item, extra }) => (
    <li className="suspect-note-item">
        <p className="suspect-note-item-head">
            <span className="suspect-note-moment">{item.momentLabel}</span>
            {extra ? ` · ${extra}` : ''}
        </p>
        <ul className="suspect-note-answers">
            {item.answers.map((answer, index) => (
                <li key={`${item.key}-${answer.rowId || index}`} className="suspect-note-answer">
                    <span className="suspect-note-answer-value">{answer.raw}</span>
                    <span className="suspect-note-answer-title">
                        {answer.title}
                        {answer.control ? ' (controlevraag)' : ''}
                    </span>
                </li>
            ))}
        </ul>
    </li>
);

/**
 * Toont welke invullingen het gemiddelde van dit effect vertekenen.
 *
 * Twee blokken, bewust verschillend van toon. De harde signalen zijn intern
 * tegenstrijdig en krijgen een waarschuwingskleur. De uitschieters zijn GEEN
 * fout: een lage score betekent meestal dat iemand het zwaar heeft, en de
 * meest consistente invulling van een organisatie is vaak juist haar laagste.
 * Die twee in één lijstje zetten suggereert dat de zwaarste deelnemers
 * verdachte gegevens zijn.
 *
 * Er wordt niets uitgesloten en niets opgeslagen — dit is alleen te lezen.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} props
 * @param {import('../../helpers/analysisHelpers/detectSuspectSubmissions').SuspectResult|null} props.suspect - Uitkomst van detectSuspectSubmissions; null zolang de gegevens laden.
 */
// Meer dan dit per blok helpt niemand meer lezen, en een bimodaal
// antwoordende groep levert er zo honderden op (gemeten: 135 + 135 bij 450
// invullingen). De rest wordt geteld, niet getekend.
const MAX_ZICHTBAAR = 10;

const SuspectSubmissionsNote = ({ suspect }) => {
    // Null betekent 'nog niet bekend'. Dan niets zeggen — geen enkele bewering
    // over afwezigheid is waar zolang de gegevens onderweg zijn.
    if (!suspect) return null;

    const { notRead = [], controlMismatch = [], outliers = [], coverage } = suspect;
    const hard = [
        ...notRead.map((item) => ({
            item, extra: `overal ${item.value} ingevuld, ook op de controlevraag`,
        })),
        ...controlMismatch.map((item) => ({
            item, extra: `controlevraag wijkt ${item.gap.toFixed(1)} punt af van de rest`,
        })),
    ];
    const nietTeToetsen = (coverage?.noControlRow || 0) + (coverage?.noFormId || 0)
        + (coverage?.noControlAnswer || 0);

    if (!hard.length && !outliers.length) return null;

    return (
        <div className="suspect-note">
            {hard.length > 0 && (
                <section className="suspect-note-block suspect-note-block--warn">
                    <h4 className="suspect-note-title">
                        {`${hard.length} ${hard.length === 1 ? 'invulling spreekt' : 'invullingen spreken'} zichzelf tegen`}
                    </h4>
                    <p className="suspect-note-lead">
                        Deze antwoorden spreken zichzelf tegen: de controlevraag is omgekeerd
                        gesteld, dus hij hoort niet hetzelfde te scoren als de rest.
                    </p>
                    <ul className="suspect-note-list">
                        {hard.slice(0, MAX_ZICHTBAAR).map(({ item, extra }) => (
                            <SuspectRow key={item.key} item={item} extra={extra} />
                        ))}
                    </ul>
                    {hard.length > MAX_ZICHTBAAR && (
                        <p className="suspect-note-coverage">
                            {`En nog ${hard.length - MAX_ZICHTBAAR} andere.`}
                        </p>
                    )}
                </section>
            )}

            {outliers.length > 0 && (
                <section className="suspect-note-block">
                    <h4 className="suspect-note-title">
                        {`${outliers.length} ${outliers.length === 1 ? 'invulling wijkt' : 'invullingen wijken'} af van de rest`}
                    </h4>
                    <p className="suspect-note-lead">
                        Dit is geen fout. Een lage score betekent meestal dat iemand het zwaar
                        heeft, niet dat de invulling niet deugt — het staat hier zodat je weet
                        waar het gemiddelde op rust.
                    </p>
                    <ul className="suspect-note-list">
                        {outliers.slice(0, MAX_ZICHTBAAR).map((item) => (
                            <SuspectRow
                                key={item.key}
                                item={item}
                                extra={`gemiddelde ${item.mean.toFixed(1)} tegenover ${item.median.toFixed(1)} voor de rest`}
                            />
                        ))}
                    </ul>
                    {outliers.length > MAX_ZICHTBAAR && (
                        <p className="suspect-note-coverage">
                            {`En nog ${outliers.length - MAX_ZICHTBAAR} andere.`}
                        </p>
                    )}
                </section>
            )}

            {nietTeToetsen > 0 && (
                <p className="suspect-note-coverage">
                    {coverage.noControlRow > 0 && `${coverage.noControlRow} invullingen konden niet getoetst worden omdat de vraag geen controlevraag heeft. `}
                    {coverage.noControlAnswer > 0 && `${coverage.noControlAnswer} invullingen lieten de controlevraag onbeantwoord. `}
                    {coverage.noFormId > 0 && `${coverage.noFormId} antwoorden horen bij geen enkele invulling en vielen buiten de controle.`}
                </p>
            )}
        </div>
    );
};

export default SuspectSubmissionsNote;
